import React, { useEffect, useRef, useCallback } from 'react'
import { cn } from '../../utils/cn'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}

interface DialogContentProps {
  className?: string
  children: React.ReactNode
}

interface DialogHeaderProps {
  className?: string
  children: React.ReactNode
}

interface DialogTitleProps {
  className?: string
  children: React.ReactNode
}

interface DialogDescriptionProps {
  className?: string
  children: React.ReactNode
}

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  useBodyScrollLock(open)
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onOpenChangeRef.current(false)
      return
    }
    if (e.key === 'Tab' && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }, [])

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement
      document.addEventListener('keydown', handleKeyDown)
      requestAnimationFrame(() => {
        const autoFocus = dialogRef.current?.querySelector<HTMLElement>(
          'input, textarea, select, [autofocus]',
        )
        const fallback = dialogRef.current?.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        ;(autoFocus || fallback)?.focus()
      })
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus()
      previousFocusRef.current = null
    }
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, handleKeyDown])

  if (!open) return null

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      // Safe-area padding on the container itself pulls the sheet above the
      // Android gesture / 3-button nav bar so sticky action buttons stay
      // tappable. Backdrop is a sibling so it still bleeds to the edges.
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{
        paddingTop: 'var(--sat, 0px)',
        paddingBottom: 'var(--sab, 0px)',
        paddingLeft: 'var(--sal, 0px)',
        paddingRight: 'var(--sar, 0px)',
      }}
    >
      <div
        // bg-overlay, not bg-black/50: --overlay is a required theme-contract
        // token and each theme tunes its own scrim (2026-08-29 UI audit).
        className="fixed inset-0 bg-overlay backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      <div className="relative z-50 w-full sm:w-auto">
        {children}
      </div>
    </div>
  )
}

export function DialogContent({ className, children }: DialogContentProps) {
  return (
    <div className={cn(
      "bg-surface shadow-lg border border-soft",
      "w-full rounded-t-2xl sm:rounded-2xl sm:max-w-lg sm:mx-4 overflow-y-auto",
      // Cap height so the sheet never grows past the safe viewport, even
      // before Tailwind's dvh is available on older WebViews.
      "max-h-[calc(100vh-var(--sat,0px)-var(--sab,0px)-16px)]",
      "sm:max-h-[85vh]",
      className
    )}>
      {children}
    </div>
  )
}

export function DialogHeader({ className, children }: DialogHeaderProps) {
  return (
    <div className={cn("p-6", className)}>
      {children}
    </div>
  )
}

export function DialogTitle({ className, children }: DialogTitleProps) {
  return (
    <h2 className={cn("text-lg font-semibold text-primary", className)}>
      {children}
    </h2>
  )
}

export function DialogDescription({ className, children }: DialogDescriptionProps) {
  return (
    <p className={cn("text-sm text-secondary", className)}>
      {children}
    </p>
  )
}
